import {format} from 'date-fns';
import {CARD_BRANDS} from './constants';
import {EXTRA_FORM_NAMES, FORM_NAMES} from 'components/AddPersonalDataForm/types';

const DOC_TYPE_WITHOUT_REQUIREMENTS = 'SD';

export type AdditionalGuestDataTypes = {
  reservation?: any;
  signature?: string;
  customFormId?: string;
  isIdOrPassportRequired?: boolean;
  front_side_scan?: string;
  back_side_scan?: string;
  document_passed?: boolean;
  biomatch_passed?: boolean;
  biomatch_doc?: string;
  biomatch_selfie?: string;
};

async function buildGuest(
  data: any,
  {
    reservation,
    signature,
    front_side_scan,
    back_side_scan,
    document_passed,
    isIdOrPassportRequired,
    biomatch_passed,
    biomatch_doc,
    biomatch_selfie,
  }: AdditionalGuestDataTypes,
) {
  const reservationId = reservation?.id;
  if (!data || !reservationId) {
    return null;
  }

  const residenceCity = data.residenceCity || undefined;
  const cityOfBirth = data.cityOfBirth || undefined;
  const cityOfIssue = data.cityOfIssue || undefined;
  const hasDocIssuePlace = data.countryOfIssue || cityOfIssue?.value || cityOfIssue;
  const hasBirthPlace = data.birthCountry || cityOfBirth;
  const hasDocument =
    data.docType ||
    data.docNumber ||
    data.visaNumber ||
    front_side_scan ||
    back_side_scan;
  const hasResidence =
    data.residenceAddress ||
    residenceCity ||
    data.residenceCountry ||
    data.residencePostalCode ||
    data.residenceProvince;
  const hasNextDestination =
    data.nextDestinationAddress ||
    data.nextDestinationCity ||
    data.nextDestinationCountry ||
    data.nextDestinationDistrict;
  const hasArrival =
    data.arrivalCountry || data.arrivalMunicipality || data.arrivalDistrict;
  const birth_date = data.birthDate
    ? format(new Date(data.birthDate), 'yyyy-MM-dd')
    : undefined;
  const issue_date = data.docDateOfIssue
    ? format(new Date(data.docDateOfIssue), 'yyyy-MM-dd')
    : undefined;
  let documents: any = hasDocument
    ? [
        {
          issue_date,
          front_side_scan,
          back_side_scan,
          type:
            data.docType?.value ||
            (isIdOrPassportRequired ? DOC_TYPE_WITHOUT_REQUIREMENTS : undefined),
          number: data.docNumber,
          issue_place: hasDocIssuePlace
            ? {
                country: data.countryOfIssue?.value,
                city: cityOfIssue?.value ? undefined : cityOfIssue,
                division_level_3: cityOfIssue?.value,
              }
            : undefined,
        },
      ]
    : undefined;
  const visaDoc = data.visaNumber
    ? {
        type: CARD_BRANDS.visa,
        number: data.visaNumber,
      }
    : undefined;

  if (visaDoc) {
    if (documents) {
      documents.push(visaDoc);
    } else {
      documents = [visaDoc];
    }
  }

  return {
    signature,
    documents,
    birth_date,
    document_passed,
    biomatch_doc,
    biomatch_selfie,
    biomatch_passed,
    origin: 'GUEST_APP',
    reservation_id: reservation.id || undefined,
    name: data.name || undefined,
    surname: data.surname || undefined,
    second_surname: data.second_surname || undefined,
    nationality: data.nationality?.value || undefined,
    citizenship: data.citizenship?.value || undefined,
    purpose_of_stay: data.purposeOfStay?.value || undefined,
    sex: data.sex?.value || undefined,
    tax_exemption: data.taxExemption?.value || undefined,
    birth_place: hasBirthPlace
      ? {
          country: data.birthCountry?.value,
          city: cityOfBirth?.value ? undefined : cityOfBirth,
          division_level_3: cityOfBirth?.value,
        }
      : undefined,
    residence: hasResidence
      ? {
          address: data.residenceAddress,
          city: residenceCity?.value ? undefined : residenceCity,
          country: data.residenceCountry?.value,
          postal_code: data.residencePostalCode,
          division_level_2: data.residenceProvince?.value,
          division_level_3: residenceCity?.value,
        }
      : undefined,
    arrived_from: hasArrival
      ? {
          country: data.arrivalCountry?.value,
          division_lvl_1: data.arrivalDistrict?.value,
          division_lvl_2: data.arrivalMunicipality?.value,
        }
      : undefined,
    next_destination: hasNextDestination
      ? {
          address: data.nextDestinationAddress,
          city: data.nextDestinationCity,
          country: data.nextDestinationCountry?.value,
          division_level_1: data.nextDestinationDistrict?.value,
          division_level_2: data.nextDestinationMunicipality?.value,
        }
      : undefined,
    email: data?.email || undefined,
    phone: data?.phone || undefined,
    fiscal_code: data?.fiscalCode || undefined,
  };
}

function getBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file) {
      return reject('File is missing');
    }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
  });
}

function getHasSomeCustomFields(formData: any) {
  const formNames = Object.values(FORM_NAMES);
  const formDataNames = Object.keys(formData);

  return formDataNames
    .filter((name: any) => {
      return !formNames.includes(name);
    })
    .some(name => {
      return Boolean(formData[name]);
    });
}

async function buildCustomFields(formData: any) {
  const formNames = Object.values({...FORM_NAMES, ...EXTRA_FORM_NAMES});
  const formDataNames = Object.keys(formData);
  const customFormNames = formDataNames.filter((name: any) => {
    return !formNames.includes(name);
  });

  const payload: {[key: string]: string} = {};

  for await (let formName of customFormNames) {
    const value = formData[formName]?.value || formData[formName];

    if (value instanceof File) {
      payload[formName] = await getBase64(value as File);
      continue;
    }

    if (value instanceof Date) {
      payload[formName] = format(new Date(value), 'yyyy-MM-dd');
      continue;
    }

    if (value) {
      payload[formName] = value;
    }
  }

  return payload;
}

const storedGuestIdStorageName = 'guestId';
function getStoredGuestId() {
  return localStorage.getItem(storedGuestIdStorageName) || '';
}

function setStoredGuestId(id: string) {
  return localStorage.setItem(storedGuestIdStorageName, id);
}

function setExperiencesModalAlreadyShowed(value = false) {
  localStorage.setItem('isExperiencesModalAlreadyShowed', JSON.stringify(value));
}

function getExperiencesModalAlreadyShowed() {
  return localStorage.getItem('isExperiencesModalAlreadyShowed' || false) === 'true';
}

export {
  buildGuest,
  buildCustomFields,
  getHasSomeCustomFields,
  getStoredGuestId,
  setStoredGuestId,
  setExperiencesModalAlreadyShowed,
  getExperiencesModalAlreadyShowed,
};
